import { OpenFeature, type Provider, type Client } from '@openfeature/web-sdk'
import { set, get, once } from 'lodash-es'

import {
  WebMultiProvider as MultiProvider,
  FirstSuccessfulStrategy,
} from '@openfeature/multi-provider-web'

import debug from '../utils/console'
import { PubSub } from '../utils/PubSub'
import type { ConfigCatWebProvider } from '@openfeature/config-cat-web-provider'
const logger = debug.withTag('openfeature')

const POLL_INTERVAL_SECONDS = 1 * 60 * 60 // 1 hour

let client: Client | null = null
function getOpenFeatureWebClient(): Client {
  logger.info('getOpenFeatureWebClient')
  if (client) {
    logger.log('Using existing OpenFeature client')
    return client
  }
  client = OpenFeature.getClient()
  if (!client) {
    throw new Error('OpenFeature client is not initialized')
  }
  return client
}

const clientAccessor = once(getOpenFeatureWebClient)

const pubsub = new PubSub()
let configCat: Provider | null = null
async function createConfigCatWebProvider(
  configCatKey: string,
  pollIntervalSeconds: number
): Promise<Provider> {
  logger.info('createConfigCatWebProvider')
  if (configCat) {
    logger.log('Using existing ConfigCat provider')
    return configCat
  }
  const config = useRuntimeConfig()

  const { ConfigCatWebProvider } = await import(
    '@openfeature/config-cat-web-provider'
  )
  const baseUrl = config.public.configCatBaseUrl
  // create an event emitter to listen for provider events
  const loaded = new Promise<ConfigCatWebProvider>((resolve, reject) => {
    pubsub.on('error', (error) => {
      logger.error('ConfigCat provider error:', error)
      reject(error)
    })
    pubsub.on('clientReady', () => {
      logger.info('ConfigCat provider is ready')
      resolve(provider)
    })
  })

  const provider =
    configCat ||
    ConfigCatWebProvider.create(configCatKey, {
      pollIntervalSeconds,
      baseUrl,
      setupHooks: (hooks) => {
        hooks.on('clientError', (error) => {
          pubsub.emit('error', error)
        })
        hooks.on('clientReady', () => {
          pubsub.emit('clientReady', provider)
        })
      },
    })
  await provider.initialize()
  await loaded
  configCat = provider
  return provider
}

function getPollIntervalSeconds(): number {
  const config = useRuntimeConfig()
  return +(config.public.configCatPollIntervalSeconds || POLL_INTERVAL_SECONDS)
}

export async function registerFeatureFlagProvider(
  options: FeatureFlagProvidersOptions
): Promise<Client> {
  try {
    logger.log('registerFeatureFlagProvider', options)
    const { configCatKey } = options
    if (configCatKey) {
      try {
        const pollIntervalSeconds = getPollIntervalSeconds()
        await OpenFeature.setProviderAndWait(
          await createConfigCatWebProvider(configCatKey, pollIntervalSeconds)
        )
        return clientAccessor()
      } catch (error) {
        logger.warn('Error initializing ConfigCat provider:', error)
      }
    } else {
      if (options.queryProvider) {
        const { QueryProvider } = await import('./QueryProvider')
        const url = useRequestURL()
        await OpenFeature.setProviderAndWait(new QueryProvider(url.href))
        return clientAccessor()
      }
      const { LocalProvider } = await import('./LocalProvider')
      await OpenFeature.setProviderAndWait(new LocalProvider())
    }
  } catch (error) {
    logger.error(error)
  }
  return clientAccessor()
}

export interface FeatureFlagProvidersOptions {
  configCatKey?: string
  localProvider?: boolean
  queryProvider?: boolean
}

export async function registerMultipleFeatureFlagProviders(
  options: FeatureFlagProvidersOptions
): Promise<Client> {
  try {
    logger.log('registerMultipleFeatureFlagProviders', options)
    const providers: { provider: Provider }[] = []
    const loaders: Promise<void>[] = []
    if (options.configCatKey) {
      const pollIntervalSeconds = getPollIntervalSeconds()
      loaders.push(
        (async () => {
          try {
            const provider = await createConfigCatWebProvider(
              String(options.configCatKey),
              pollIntervalSeconds
            )
            providers.push({ provider })
          } catch (error) {
            logger.warn('ConfigCat provider failed to initialize:', error)
          }
        })()
      )
    }
    if (options.localProvider) {
      loaders.push(
        (async () => {
          const { LocalProvider } = await import('./LocalProvider')
          providers.push({
            provider: new LocalProvider(),
          })
        })()
      )
    }

    if (options.queryProvider) {
      // Ensure QueryProvider takes precedence.
      loaders.push(
        (async () => {
          const { QueryProvider } = await import('./QueryProvider')
          const url = useRequestURL()
          providers.unshift({
            provider: new QueryProvider(url.href),
          })
        })()
      )
    }
    await Promise.all(loaders)
    if (providers.length === 0) {
      throw new Error('No providers provided')
    }
    const multiProvider = new MultiProvider(
      providers,
      // new ComparisonStrategy(
      //   providers[providers.length - 1].provider,
      //   (details) => {
      //     logger.info('Comparison strategy called with details:', details)
      //   }
      // )
      new FirstSuccessfulStrategy()
    )
    try {
      await OpenFeature.setProviderAndWait(multiProvider)
    } catch (error) {
      logger.error('Error initializing providers:', error)
      /**empty*/
    }
  } catch (error) {
    /**
     * @todo this should send an alert to monitoring (probably Datadog)
     */
    logger.error(error)
  }

  const client = clientAccessor()
  // Only expose client on window object in development mode
  set(window, 'openfeature', client)

  logger.log('openfeature', client)
  return client
}

/**
 * Teardown function to clear OpenFeature client and providers.
 */
export async function teardown() {
  logger.log('Clearing OpenFeature client and providers')
  if (typeof window !== 'undefined' && get(window, 'openfeature')) {
    logger.log('Clearing existing OpenFeature client')
    set(window, 'openfeature', null)
  }
  await OpenFeature.clearProviders()
  if (configCat) {
    logger.log('Clearing existing ConfigCat provider')
    configCat = null
  }
}
